import { useEffect, useMemo, useState } from 'react';
import { MutationError } from '@elementary/common-ui/types/mutationTypes';
import { useMutation } from '@tanstack/react-query';
import { useForm, FormProvider } from 'react-hook-form';
import useInvalidateAlertsRelatedData from 'shared/hooks/useInvalidateAlertsRelatedData';
import useSnackbar from 'shared/hooks/useSnackbar';
import { buildCron, parseCron } from 'shared/utils/cronUtils';
import RuleDrawer from '../../../components/ruleDrawer/RuleDrawer';
import {
  alertFilterModelsToAlertFilters,
  alertFiltersToAlertFilterModels,
} from '../../../utils/filterConverters';
import * as api from '../../services/api';
import { DigestRuleForm, DigestRuleModel } from '../../types';

const DEFAULT_CRON: ReturnType<typeof parseCron> = {
  cadenceType: 'daily',
  hour: 9,
};

type Props = {
  digestRule?: DigestRuleModel;
  onClose: () => void;
  open: boolean;
  envId: string;
};

const DigestRuleDrawer = ({ digestRule, onClose, open, envId }: Props) => {
  const [errorMessage, setErrorMessage] = useState<string>();

  const { openSnackbar } = useSnackbar();
  const { invalidateAlertsDataQueries } = useInvalidateAlertsRelatedData();

  const isEditMode = useMemo(() => digestRule?.id != null, [digestRule]);

  const defaultValues = useMemo((): Partial<DigestRuleForm> => {
    const cronParsed = digestRule?.cron
      ? parseCron(digestRule.cron)
      : DEFAULT_CRON;

    const destinations =
      digestRule?.destinations?.map((d) => ({
        integrationId: d.alert_integration_id,
        destinationId: d.id,
        channelName: d.channel_name,
        type: d.alert_integration_type,
      })) || [];

    return {
      ruleId: digestRule?.id,
      name: digestRule?.name,
      cadenceType: cronParsed.cadenceType,
      cadenceHour: cronParsed.hour,
      cadenceDay: cronParsed.day,
      incidentFilters: alertFiltersToAlertFilterModels(
        digestRule?.incident_filters,
      ),
      assetFilters: alertFiltersToAlertFilterModels(digestRule?.asset_filters),
      incidentCategories: digestRule?.incident_categories,
      destinations,
    };
  }, [digestRule]);

  const methods = useForm<DigestRuleForm>({ defaultValues, mode: 'onChange' });

  const { mutate: createDigestRule, isLoading: isCreateLoading } = useMutation(
    (data: DigestRuleForm) => {
      const cron = buildCron(
        data.cadenceType,
        data.cadenceHour,
        data.cadenceDay,
      );
      const reqData = {
        name: data.name,
        incident_filters: data.incidentFilters?.length
          ? alertFilterModelsToAlertFilters(data.incidentFilters)
          : [],
        asset_filters: data.assetFilters?.length
          ? alertFilterModelsToAlertFilters(data.assetFilters)
          : [],
        incident_categories: data.incidentCategories,
        cron,
        destinations: data.destinations.map((d) => ({
          channel_name: d.channelName,
          alert_integration_id: d.integrationId,
        })),
      };
      return api.createDigestRule(reqData, envId!);
    },
    {
      onSuccess: () => {
        onClose();
        invalidateAlertsDataQueries();
        openSnackbar({
          message: 'Digest rule created successfully!',
          variant: 'success',
        });
      },
      onError: (error: MutationError) => {
        setErrorMessage(
          (error.response?.data?.error_message as string) || 'Error',
        );
      },
    },
  );

  const { mutate: updateDigestRule, isLoading: isUpdateLoading } = useMutation(
    (data: DigestRuleForm) => {
      const cron = buildCron(
        data.cadenceType,
        data.cadenceHour,
        data.cadenceDay,
      );
      const reqData = {
        name: data.name,
        incident_filters: data.incidentFilters?.length
          ? alertFilterModelsToAlertFilters(data.incidentFilters)
          : [],
        asset_filters: data.assetFilters?.length
          ? alertFilterModelsToAlertFilters(data.assetFilters)
          : [],
        incident_categories: data.incidentCategories,
        cron,
        destinations: data.destinations.map((d) => ({
          id: d.destinationId,
          channel_name: d.channelName,
          alert_integration_id: d.integrationId,
        })),
      };
      return api.updateDigestRule(reqData, envId!, data.ruleId!);
    },
    {
      onSuccess: () => {
        onClose();
        invalidateAlertsDataQueries();
        openSnackbar({
          message: 'Digest rule updated successfully!',
          variant: 'success',
        });
      },
      onError: (error: MutationError) => {
        setErrorMessage(
          (error.response?.data?.error_message as string) || 'Error',
        );
      },
    },
  );

  useEffect(() => {
    if (open) {
      methods.reset?.(defaultValues);
      setErrorMessage(undefined);
    }
  }, [open, defaultValues, methods]);

  const onSubmit = (newData: DigestRuleForm) => {
    if (isEditMode) {
      updateDigestRule(newData);
    } else {
      createDigestRule(newData);
    }
  };

  return (
    <FormProvider {...methods}>
      <RuleDrawer
        open={open}
        onClose={onClose}
        envId={envId}
        title="digest rule"
        namePlaceholder="e.g. Daily failure digest"
        onSubmit={methods.handleSubmit(onSubmit)}
        isLoading={isEditMode ? isUpdateLoading : isCreateLoading}
        isEditMode={isEditMode}
        errorMessage={errorMessage}
        showCadence
      />
    </FormProvider>
  );
};

export default DigestRuleDrawer;
